import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { ThemeToggle } from '../components/app-shell.tsx';
import { LogoMark } from '../components/nav/nav-parts.tsx';
import { Button } from '../components/ui/button.tsx';
import { Input } from '../components/ui/input.tsx';
import { Label } from '../components/ui/label.tsx';
import { LOGIN_TEXTS, LoginError, stripCodeSpaces, type AuthClient, type LoginStep } from '../lib/auth.ts';

type OpenStep = Exclude<LoginStep, { kind: 'done' }>;

interface Props {
  /** Aktueller Schritt; null, wenn der Server nicht erreichbar ist (nur Meldung). */
  step: OpenStep | null;
  message: string | null;
  auth: AuthClient | null;
  /** Nach einem erfolgreichen Schritt: Anmeldung neu bewerten. */
  onAdvance: () => Promise<void>;
}

/** Schritt-Pille über der Überschrift (Entwurf A01, Ticket 1.243). */
const STEP_TEXTS = {
  password: 'Schritt 1',
  verify: 'Schritt 2 · nur wenn der Admin den zweiten Faktor verlangt',
  enroll: 'Schritt 2 · zweiten Faktor einrichten',
} as const;

function StepPill({ children }: { children: string }) {
  return <span className="inline-flex self-center rounded-full bg-surface-card px-space-3 py-space-1 text-caption font-medium text-ink-2">{children}</span>;
}

/**
 * Anmeldeseite: E-Mail + Passwort, dann – falls eingeschaltet – zweiter Faktor (einrichten oder Code eingeben).
 * Aufbau nach Entwurf A01 (Ticket 1.243): Logo-Kreis und Schriftzug über der Karte, Schritt-Pille, Überschrift 26 px.
 */
export function LoginPage({ step, message, auth, onAdvance }: Props) {
  return (
    // Redesign OpenAI-Stil (N40): wie die Anmeldung bei OpenAI – weiße Fläche ohne Karte, Zeichen und Schriftzug mittig
    // oben, große ruhige Überschrift, breite Felder, ein dunkelblauer Hauptknopf über die ganze Breite.
    <main className="relative flex min-h-screen flex-col items-center px-space-6 pt-24 pb-space-8 tablet:justify-center tablet:pt-space-8">
      <div className="absolute top-space-6 right-space-6">
        <ThemeToggle />
      </div>
      <div className="flex animate-fade-in flex-col items-center gap-space-3">
        <span aria-hidden>
          <LogoMark />
        </span>
        <span className="type-wordmark whitespace-nowrap text-ink-2">TILL TOOLS ERP</span>
      </div>
      {/* Karte direkt unter <main> (Prüfung G8 sucht `main > section`). */}
      <section className="mt-space-8 flex w-full max-w-sm animate-fade-in flex-col [&_h1]:text-center [&>p]:text-center">
        {step && auth ? (
          // key: jeder Schritt beginnt mit leerem Formular und ohne alten Fehler.
          <StepForm
            key={step.kind === 'password' ? 'password' : `${step.kind}-${step.factorId}`}
            step={step}
            auth={auth}
            message={message}
            onAdvance={onAdvance}
          />
        ) : (
          <>
            <h1 className="mt-space-4 text-page-title leading-tight font-semibold tracking-[-0.025em]">Anmelden</h1>
            <Alert text={message} />
          </>
        )}
      </section>
    </main>
  );
}

function StepForm({ step, auth, message, onAdvance }: { step: OpenStep; auth: AuthClient; message: string | null; onAdvance: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<LoginError | null>(null);
  const [forgot, setForgot] = useState(false);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await onAdvance();
    } catch (err) {
      setError(err instanceof LoginError ? err : new LoginError(LOGIN_TEXTS.general));
    } finally {
      // Auch wenn die Bewertung auf denselben Schritt zurückführt (gleicher key), Knopf wieder frei.
      setBusy(false);
    }
  }

  const formError = error && error.field === null ? error.message : null;
  const passwordError = error?.field === 'password' ? error.message : null;
  const codeError = error?.field === 'code' ? error.message : null;
  const cancel = (
    <button type="button" className="mt-space-5 min-h-6 cursor-pointer self-center text-body font-medium text-ink-2 hover:text-ink touch-hit" onClick={() => void auth.signOut()}>
      Mit anderem Konto anmelden
    </button>
  );

  if (step.kind === 'password') {
    if (forgot) return <ForgotForm auth={auth} onBack={() => setForgot(false)} />;
    return (
      <PasswordForm
        busy={busy}
        message={formError ?? message}
        error={passwordError}
        onSubmit={(email, password) => void run(() => auth.signInWithPassword(email, password))}
        onForgot={() => {
          setError(null);
          setForgot(true);
        }}
      />
    );
  }
  if (step.kind === 'enroll') {
    return (
      <>
        <StepPill>{STEP_TEXTS.enroll}</StepPill>
        <h1 className="mt-space-4 text-page-title leading-tight font-semibold tracking-[-0.025em]">Zweiten Faktor einrichten</h1>
        <p className="type-meta mt-2 text-ink-3">
          Damit niemand mit deinem Passwort allein ins ERP kommt, brauchst du zusätzlich einen Code aus einer Authenticator-App auf deinem Handy.
        </p>
        <Alert text={formError ?? message} />
        <ol className="mt-6 flex list-none flex-col gap-5 p-0 text-row">
          <li>
            <span className="text-ink-2">1.</span> Öffne die Authenticator-App und füge ein neues Konto hinzu (meist mit „+“).
          </li>
          <li>
            <span className="text-ink-2">2.</span> Scanne diesen QR-Code:
            <div className="mt-3 flex justify-center">
              {/* QR bleibt auch im Dunkelmodus schwarz auf weiß, sonst liest die Kamera ihn nicht. */}
              <img src={step.qrCode} alt="QR-Code für die Authenticator-App" width={180} height={180} className="rounded-tile bg-paper p-3" />
            </div>
            <p className="type-meta mt-3 text-ink-3">Scannen geht nicht? Gib in der App stattdessen diesen Schlüssel von Hand ein:</p>
            <code aria-label="Schlüssel zum Abtippen" className="mt-2 block rounded-field bg-tile px-4 py-3 text-row tracking-key break-all select-all">
              {groupSecret(step.secret)}
            </code>
          </li>
          <li>
            <span className="text-ink-2">3.</span> Gib den 6-stelligen Code ein, den die App jetzt für „TILL ERP“ zeigt.
          </li>
        </ol>
        <CodeForm
          busy={busy}
          error={codeError}
          submitLabel="Einrichten und anmelden"
          onSubmit={(code) => void run(() => auth.verifyCode(step.factorId, code))}
        />
        {cancel}
      </>
    );
  }
  return (
    <>
      <StepPill>{STEP_TEXTS.verify}</StepPill>
      <h1 className="mt-space-4 text-page-title leading-tight font-semibold tracking-[-0.025em]">Code eingeben</h1>
      <p className="mt-space-3 text-row text-ink-2">Öffne deine Authenticator-App und gib den 6-stelligen Code für „TILL ERP“ ein.</p>
      <Alert text={formError ?? message} />
      <CodeForm busy={busy} error={codeError} submitLabel="Anmelden" onSubmit={(code) => void run(() => auth.verifyCode(step.factorId, code))} />
      {cancel}
    </>
  );
}

function PasswordForm({
  busy,
  message,
  error,
  onSubmit,
  onForgot,
}: {
  busy: boolean;
  message: string | null;
  error: string | null;
  onSubmit: (email: string, password: string) => void;
  onForgot: () => void;
}) {
  const emailId = useId();
  const passwordId = useId();
  const errorId = useId();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  return (
    <>
      <StepPill>{STEP_TEXTS.password}</StepPill>
      <h1 className="mt-space-4 text-page-title leading-tight font-semibold tracking-[-0.025em]">Anmelden</h1>
      <p className="mt-space-3 text-row text-ink-2">
        Mit deiner E-Mail-Adresse und deinem Passwort. Ist der zweite Faktor eingeschaltet, fragt das ERP danach nach dem Code aus deiner Authenticator-App.
      </p>
      <Alert text={message} />
      <form
        className="mt-space-8 flex flex-col gap-space-5"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          onSubmit(email, password);
        }}
      >
        <div>
          <Label htmlFor={emailId}>E-Mail-Adresse</Label>
          <Input id={emailId} type="email" name="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <Label htmlFor={passwordId}>Passwort</Label>
          <Input
            id={passwordId}
            type="password"
            name="password"
            autoComplete="current-password"
            required
            invalid={!!error}
            aria-describedby={error ? errorId : undefined}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error ? (
            <p id={errorId} role="alert" className="type-meta mt-2 text-clarify">
              {error}
            </p>
          ) : null}
        </div>
        <Button type="submit" className="w-full" disabled={busy}>
          Anmelden
        </Button>
      </form>
      <button type="button" className="mt-space-5 min-h-6 cursor-pointer self-center text-body font-medium text-ink-2 hover:text-ink touch-hit" onClick={onForgot}>
        Passwort vergessen?
      </button>
      <p className="mt-space-2 text-center text-caption text-ink-3">Handy verloren? Der Admin kann den zweiten Faktor zurücksetzen.</p>
    </>
  );
}

/** „Passwort vergessen“: Supabase schickt einen Link; er führt auf die Seite /einrichten. */
function ForgotForm({ auth, onBack }: { auth: AuthClient; onBack: () => void }) {
  const emailId = useId();
  const errorId = useId();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <h1 className="mt-space-4 text-page-title leading-tight font-semibold tracking-[-0.025em]">Passwort vergessen</h1>
      <p className="mt-space-3 text-row text-ink-2">Gib deine E-Mail-Adresse ein. Du bekommst einen Link, mit dem du ein neues Passwort festlegst.</p>
      {sent ? (
        <p role="status" className="type-meta mt-5 rounded-tile bg-tile px-4 py-3 text-ink">
          {LOGIN_TEXTS.resetSent}
        </p>
      ) : (
        <form
          className="mt-space-8 flex flex-col gap-space-5"
          noValidate
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (!email.trim()) {
              setError(LOGIN_TEXTS.emailMissing);
              return;
            }
            setBusy(true);
            setError(null);
            auth
              .requestPasswordReset(email)
              .then(() => setSent(true))
              .catch((err: unknown) => setError(err instanceof LoginError ? err.message : LOGIN_TEXTS.general))
              .finally(() => setBusy(false));
          }}
        >
          <div>
            <Label htmlFor={emailId}>E-Mail-Adresse</Label>
            <Input
              id={emailId}
              type="email"
              name="email"
              autoComplete="username"
              autoFocus
              invalid={!!error}
              aria-describedby={error ? errorId : undefined}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {error ? (
              <p id={errorId} role="alert" className="type-meta mt-2 text-clarify">
                {error}
              </p>
            ) : null}
          </div>
          <Button type="submit" className="w-full" disabled={busy}>
            Link senden
          </Button>
        </form>
      )}
      <button type="button" className="mt-space-5 min-h-6 cursor-pointer self-center text-body font-medium text-ink-2 hover:text-ink touch-hit" onClick={onBack}>
        Zurück zur Anmeldung
      </button>
    </>
  );
}

function CodeForm({ busy, error, submitLabel, onSubmit }: { busy: boolean; error: string | null; submitLabel: string; onSubmit: (code: string) => void }) {
  const id = useId();
  const errorId = useId();
  const [code, setCode] = useState('');
  return (
    <form
      className="mt-space-8 flex flex-col gap-space-5"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        onSubmit(stripCodeSpaces(code));
      }}
    >
      <div>
        <Label htmlFor={id}>Code aus der App</Label>
        <OneTimeCodeInput id={id} value={code} onChange={setCode} invalid={!!error} describedBy={error ? errorId : undefined} />
        {error ? (
          <p id={errorId} role="alert" className="type-meta mt-2 text-clarify">
            {error}
          </p>
        ) : null}
      </div>
      <Button type="submit" className="w-full" disabled={busy}>
        {submitLabel}
      </Button>
    </form>
  );
}

/** Höchstlänge des Code-Felds: Platz für „123 456“ und ähnliche Schreibweisen mit Leerzeichen. */
const CODE_MAX_LENGTH = 12;

/**
 * Feld „Code aus der App“ (Anmeldung und Einrichtung): Zifferntastatur und Code-Vorschlag des Handys
 * (`inputMode="numeric"`, `autocomplete="one-time-code"`). Eintippen und Einfügen gehen auch mit Leerzeichen
 * („123 456“); im Feld steht der Code ohne Leerzeichen.
 */
export function OneTimeCodeInput({
  id,
  value,
  onChange,
  invalid,
  describedBy,
}: {
  id: string;
  value: string;
  onChange: (code: string) => void;
  invalid: boolean;
  describedBy?: string;
}) {
  return (
    <Input
      id={id}
      name="code"
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={CODE_MAX_LENGTH}
      required
      autoFocus
      invalid={invalid}
      aria-describedby={describedBy}
      className="tracking-code tabular-nums"
      value={value}
      onChange={(e) => onChange(stripCodeSpaces(e.target.value))}
      onPaste={(e) => {
        // Eingefügt („123 456“ oder „Ihr Code lautet 123 456“): nur die Ziffern, höchstens so viele, wie das Feld fasst.
        e.preventDefault();
        const input = e.currentTarget;
        const digits = e.clipboardData.getData('text').replace(/\D+/g, '');
        const start = input.selectionStart ?? input.value.length;
        const end = input.selectionEnd ?? input.value.length;
        onChange(stripCodeSpaces(input.value.slice(0, start) + digits + input.value.slice(end)).slice(0, CODE_MAX_LENGTH));
      }}
    />
  );
}

function Alert({ text }: { text: string | null }): ReactNode {
  if (!text) return null;
  return (
    <p role="alert" className="type-meta mt-5 rounded-tile bg-clarify-bg px-4 py-3 text-clarify">
      {text}
    </p>
  );
}

/** Schlüssel in Vierergruppen, damit man ihn leichter abtippt. */
function groupSecret(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim();
}
